// Category tree helpers (categories come flat with parentCategoryIds)
export const findCategory = (list, categoryId) =>
  list?.find((cat) => cat.categoryId === categoryId)

const hasParent = (cat, parentId) =>
  (cat.parentCategoryIds && cat.parentCategoryIds.includes(parentId)) ||
  cat.parentCategoryId === parentId

// Root: no parent. The categories hang under one taxonomy node of their own
// ("Store"), which the api answers without — a category directly under it
// arrives here with no parent at all, as it always did.
export const isRootCategory = (cat) =>
  (!cat.parentCategoryIds || cat.parentCategoryIds.length === 0) && !cat.parentCategoryId

// Hide when productCount is explicitly 0; undefined means "unknown, show"
export const hasProducts = (cat) => cat.productCount === undefined || cat.productCount > 0

// Root → leaf chain of category objects
export function buildCategoryPath(categoryId, list) {
  if (!categoryId || !list) return []
  const path = []
  let current = categoryId
  let depth = 10
  while (current && depth-- > 0) {
    const cat = findCategory(list, current)
    if (!cat) break
    path.unshift(cat)
    current = cat.parentCategoryIds?.[0] || cat.parentCategoryId
  }
  return path
}

/**
 * The path a visitor took, from the slugs of /category/<a>/<b>/<c>: the
 * categories when each one hangs under the one before (a category under two
 * parents — Music for kids, under Music and under For kids — is reached by
 * either), else null.
 */
export function trailOf(slugs, list) {
  if (!slugs?.length || !list?.length) return null
  const trail = []
  for (const slug of slugs) {
    const cat = findCategory(list, slug)
    if (!cat) return null
    if (trail.length && !hasParent(cat, trail[trail.length - 1].categoryId)) return null
    trail.push(cat)
  }
  return isRootCategory(trail[0]) ? trail : null
}

/**
 * The categories from `ancestorId` down to `categoryId` (both included),
 * following any of the parents; null when it is not under it.
 */
export function pathBetween(ancestorId, categoryId, list) {
  if (!ancestorId || !categoryId || !list?.length) return null
  if (ancestorId === categoryId) {
    const cat = findCategory(list, categoryId)
    return cat ? [cat] : null
  }
  const queue = [[categoryId]]
  const seen = new Set([categoryId])
  while (queue.length) {
    const chain = queue.shift()
    const cat = findCategory(list, chain[0])
    if (!cat) continue
    for (const parent of cat.parentCategoryIds || (cat.parentCategoryId ? [cat.parentCategoryId] : [])) {
      if (seen.has(parent)) continue
      seen.add(parent)
      const next = [parent, ...chain]
      if (parent === ancestorId) return next.map((id) => findCategory(list, id)).filter(Boolean)
      queue.push(next)
    }
  }
  return null
}

/** "/category/a/b/c" of a path of categories. */
export const categoryUrlOf = (path) => "/category/" + path.map((cat) => cat.categoryId).join("/")

// "/category/parent/child" (or /categories when unknown)
export function buildCategoryUrl(categoryId, list) {
  const path = buildCategoryPath(categoryId, list)
  if (path.length === 0) return categoryId ? `/category/${categoryId}` : "/categories"
  return "/category/" + path.map((cat) => cat.categoryId).join("/")
}

export const rootCategories = (list) => (list || []).filter(isRootCategory)

export const childCategories = (list, parentId) =>
  (list || []).filter((cat) => hasParent(cat, parentId))

// A category and all of its descendants (ids) — used to list a whole subtree
export function collectCategoryIds(categoryId, list) {
  if (!categoryId) return []
  const ids = [categoryId]
  for (const child of childCategories(list, categoryId)) {
    ids.push(...collectCategoryIds(child.categoryId, list))
  }
  return ids
}
